import LassoDataTable from "./lasso_data_table"
import ProgressBar from "./progress_bar"

// A hardcoded literal with no interpolation, no user data and no <script> tag - safe to inject
// directly (S4: not the same as a server response or user-entered content). Same markup as
// contacts_controller.js and order_recipients_controller.js, which carry the identical comment.
const SELECT_ALL_DROPDOWN_HTML = `
  <div class="dropdown select-all btn-group">
    <button id="select-all-dropdown" class="btn-dropdown btn btn-borderless btn-transparent dropdown-toggle btn-sm" data-toggle="dropdown" type="button">
      <div class="form-group top-level">
        <input class="select-all" name="select-all" type="checkbox" aria-label="Select multiple elements">
        <span class="badge">(0)</span>
        <span class="caret"></span>
      </div>
    </button>
    <ul class="dropdown-menu">
      <li>
        <div class="form-group">
          <div class="checkbox inline">
            <input id="select-all-elements" class="boolean optional" type="checkbox" aria-label="Select all elements">
            <label class="boolean optional" for="select-all-elements">All Recipients</label>
          </div>
        </div>
      </li>
      <li>
        <div class="form-group">
          <div class="checkbox inline">
            <input id="select-this-page" class="boolean optional" type="checkbox" aria-label="Select this page">
            <label class="boolean optional" for="select-this-page">This Page</label>
          </div>
        </div>
      </li>
    </ul>
  </div>
`;

// The "select contacts" modal and its datatable, extracted out of give_recipient_controller.js
// (R9 - this owns its own filter/select2/daterangepicker/datatable state, independent of the
// Stimulus controller's recipient-row state). Its only consumer is give_recipient_controller.js,
// which constructs it once in connect() and passes callbacks in rather than reaching back into
// the controller directly, so no teardown handle needs to thread through here.
//
// This class is a thin orchestrator over three jQuery plugins (select2, daterangepicker, the
// DataTables wrapper via LassoDataTable) plus a Bootstrap modal, so it stays on jQuery throughout
// rather than a partial R8 retreat - splitting plugin setup from the plain-DOM reads/writes that
// feed those same plugin calls (e.g. reading a select2 selection, or the row checkboxes the
// DataTable redraws) would scatter one cohesive interaction across two idioms for no safety gain.
export default class SelectContacts {
  constructor(element, params) {
    if (!params ||
      !params.maxRecipients ||
      !params.existingContactsCountFn ||
      !params.addSelectedContactsCallback) {
      throw new Error("SelectContacts requires maxRecipients parameter");
    }

    this.element = $(element);

    this.$contactsDatatable = $("#contacts-datatable");
    this.$addSelectedButton = $("#add-contacts");
    this.$onNextEventCheckbox = this.element.find("#on_next_event");

    this.$filterEvent = $("#filter_event");
    this.$filterContactList = $("#filter_contact_list");
    this.$dateRangePicker = $('input[name="event-date-range-picker"]');

    this.dataTableInitialSource = this.$contactsDatatable.attr("data-source");
    this.url = this.element.data("url");

    this.sendToIds = new Set();
    this.exceptIds = new Set();

    this.allCheckboxChecked = false;
    this.pageCheckboxCheckedNum = null;

    this.maxRecipients = params.maxRecipients;

    this.getExistingRecipientsCount = params.existingContactsCountFn;
    this.addSelectedContactsCallback = params.addSelectedContactsCallback;

    this.initialize();
  }

  initialize() {
    this.initDataTable();
    this.initFilters();
    this.initEventHandlers();
  }

  initDataTable() {
    this.contactsDataTable = new LassoDataTable(this.$contactsDatatable, {
      "columns": [
        { "data": "id", "searchable": false, "orderable": false, "className": "hidden" },
        { "data": "checkbox", "searchable": false, "orderable": false, "className": "checkbox" },
        { "data": "first_name", "className": "name" },
        { "data": "last_name", "className": "hidden" },
        { "data": "email", "className": "hidden" },
        { "data": "group_list", "orderable": false, "searchable": false, "className": "group-list" },
        { "data": "next_event", "orderable": false, "className": "next_event" },
      ],
      "order": [[2, "asc"], [0, "asc"]],
      "pageLength": 1000,
      "fnDrawCallback": () => {
        const $bulkSelectCheckbox = this.element.find("input.select-all");
        const $allCheckbox = $("#select-all-elements");
        const $pageCheckbox = $("#select-this-page");

        this.$contactsDatatable.find("thead th.checkbox").html(SELECT_ALL_DROPDOWN_HTML);

        if (this.allCheckboxChecked) {
          $bulkSelectCheckbox.prop("checked", true);
          $allCheckbox.prop("checked", true);
        } else if (this.pageCheckboxCheckedNum !== null &&
          this.pageCheckboxCheckedNum === this.getPageNumber()) {
          $bulkSelectCheckbox.prop("checked", true);
          $pageCheckbox.prop("checked", true);
        }

        this.setCheckedCount();

        $(".contact-checkbox").each((i, checkbox) => {
          const $el = $(checkbox);
          const id = $el.data("id");

          if (this.allCheckboxChecked && !this.exceptIds.has(id)) {
            $el.prop("checked", true);
          } else if (this.sendToIds.has(id)) {
            $el.prop("checked", true);
          }
        });
      },
    });
  }

  initFilters() {
    this.$filterEvent.select2({
      placeholder: "Next event date",
      width: "100%",
      allowClear: true,
      minimumResultsForSearch: -1
    });

    this.$filterEvent.on("change", () => {
      this.resetSelections();
      this.reload();
    });

    this.$filterContactList.select2({
      placeholder: "All Contacts",
      width: "100%",
      allowClear: false,
      multiple: true,
      minimumResultsForSearch: -1
    });

    this.$filterContactList.on("change", (event) => {
      this.resetSelections();

      $(event.currentTarget).find(":selected").each((_, option) => {
        const $option = $(option);
        const colorClass = $option.data("color");

        $("#select-contacts-modal .select2")
          .find(`.select2-selection__choice[title='${$.escapeSelector($option.text())}']`)
          .addClass(colorClass);
      });

      this.reload();
    });

    this.$dateRangePicker.daterangepicker({
      buttonClasses: "btn btn-main btn-md",
      cancelButtonClasses: "btn-ghost",
      opens: "left",
      alwaysShowCalendars: true,
      showCustomRangeLabel: false,
      minDate: moment().subtract(183, "days"),
      maxDate: moment().add(182, "days"),
    }, (start, end) => {
      const newRange = start.format("MMDDYYYY") + "-" + end.format("MMDDYYYY");
      this.resetSelections();
      this.reload({ range: newRange });
    });

    this.$dateRangePicker.on("show.daterangepicker", (ev, picker) => {
      const $clearButton = $('<button class="btn btn-borderless clearBtn">Clear All</button>');

      $clearButton.on("click", () => {
        picker.setStartDate(moment().format("MM/DD/YYYY"));
        picker.setEndDate(moment().format("MM/DD/YYYY"));
        picker.hide();

        this.resetSelections();
        this.$contactsDatatable.attr("data-source", this.dataTableInitialSource);
        this.reload();
      });

      $clearButton.insertAfter($(".drp-selected"));
    });

    this.$dateRangePicker.on("hide.daterangepicker", () => $(".clearBtn").remove());
  }

  initEventHandlers() {
    this.element.on("click", "#select-all-elements, #select-this-page", (event) => this.handleSelectAllPage(event));
    this.element.on("change", ".contact-checkbox", (event) => this.handleContactCheckboxChange(event));
    this.$addSelectedButton.on("click", (event) => this.addSelectedContacts(event));
  }

  handleSelectAllPage(event) {
    const $allCheckbox = $("#select-all-elements");
    const $pageCheckbox = $("#select-this-page");

    const $bulkSelectCheckbox = this.element.find("input.select-all");
    const $selectedOptionCheckbox = $(event.currentTarget);

    const optionChecked = $selectedOptionCheckbox.prop("checked");
    const allContactsChecked = $allCheckbox.attr("id") === $selectedOptionCheckbox.attr("id");
    const pageContactsChecked = $pageCheckbox.attr("id") === $selectedOptionCheckbox.attr("id");

    let totalRecipientCountAfterSelection = 0;

    if (allContactsChecked) {
      totalRecipientCountAfterSelection = this.getTotalRecordsCount() + this.getExistingRecipientsCount();
    } else if (pageContactsChecked) {
      totalRecipientCountAfterSelection = this.getPageRecordsCount() + this.getExistingRecipientsCount();
    }

    if (this.maxRecipients && totalRecipientCountAfterSelection > this.maxRecipients) {
      event.preventDefault();

      const allowedRecipientsCount = this.maxRecipients - this.getExistingRecipientsCount();

      window.ltAlert({
        message: `You can only add up to ${allowedRecipientsCount} recipients.`,
        type: "toast-warning",
        duration: 5000,
      });

      return;
    }

    if (allContactsChecked) {
      this.allCheckboxChecked = !!$allCheckbox.prop("checked");
      $pageCheckbox.prop("checked", false);
      this.pageCheckboxCheckedNum = null;
    }

    if (pageContactsChecked) {
      if ($pageCheckbox.prop("checked")) {
        this.pageCheckboxCheckedNum = this.getPageNumber();
      } else {
        this.pageCheckboxCheckedNum = null;
      }
      $allCheckbox.prop("checked", false);
      this.allCheckboxChecked = false;
    }

    this.sendToIds.clear();
    this.exceptIds.clear();

    this.element.find(".contact-checkbox").each((_, el) => {
      $(el).prop("checked", optionChecked);

      if (pageContactsChecked) {
        const id = $(el).data("id");
        optionChecked ? this.sendToIds.add(id) : this.sendToIds.delete(id);
      }
    });

    $bulkSelectCheckbox.prop("checked", optionChecked);
    this.setCheckedCount();
  }

  handleContactCheckboxChange(event) {
    const $checkbox = $(event.currentTarget);
    const id = $checkbox.data("id");
    const addingSelection = $checkbox.is(":checked");

    if (addingSelection && ((this.getExistingRecipientsCount() + this.getNumCheckedContacts()) >= this.maxRecipients)) {
      window.ltAlert({
        message: `You can only add up to ${this.maxRecipients} contacts.`,
        type: "toast-warning",
        duration: 5000,
      });

      $checkbox.prop("checked", false);
      return;
    }

    if (this.allCheckboxChecked) {
      addingSelection ? this.exceptIds.delete(id) : this.exceptIds.add(id);
    } else {
      addingSelection ? this.sendToIds.add(id) : this.sendToIds.delete(id);
    }

    this.setCheckedCount();
  }

  async addSelectedContacts(event) {
    event.preventDefault();
    const $button = $(event.currentTarget);

    $button.attr("disabled", true);
    const response = await this.postContacts();
    $button.attr("disabled", false);

    const data = await response.json();

    if (!response.ok) {
      this.alertError(`There was an error adding the selected contacts: ${data?.message || "Unknown error"}`);
    }

    this.addSelectedContactsCallback(data.html, this.getNumCheckedContacts());
    this.closeModal();
  }

  urlWithFilters() {
    return this.url + "?" + this.filtersApplied();
  }

  filtersApplied() {
    return this.$contactsDatatable.attr("data-source").split("?")[1];
  }

  postContacts() {
    return fetch(this.urlWithFilters(), {
      method: "POST",
      body: JSON.stringify(this.prepareParams()),
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": $('meta[name="csrf-token"]').attr("content"),
      },
    });
  }

  prepareParams() {
    const params = {};

    if (this.sendToIds.size > 0) {
      params.contact_ids = Array.from(this.sendToIds);
    } else {
      params.except_ids = Array.from(this.exceptIds);
    }

    params.on_next_event = this.$onNextEventCheckbox.is(":checked");
    params.search = this.searchTerm();

    return params;
  }

  // Scoped to this.$contactsDatatable / this.element rather than a page-global lookup.
  reload(extraParams = {}) {
    ProgressBar.start();

    const filter = {};
    this.element.find(".datatable-moveable-filter, .datatable-filter").each((_, el) => {
      const $select = $(el);
      const selected = ($select.select2("data") || []).map((option) => option.id);
      filter[$select.attr("name").replace(/(^.*\[|\].*$)/g, "")] = selected;
    });
    if (Object.keys(filter).length > 0) { extraParams["filter"] = filter; }

    this.element.find(".datatable-moveable-text-filter, .datatable-text-filter").each((_, el) => {
      const $text = $(el);
      extraParams["filter"] = extraParams["filter"] || {};
      extraParams["filter"][$text.attr("name").replace(/(^.*\[|\].*$)/g, "")] = $text.val();
    });

    const params = Object.assign(this.currentParams(), extraParams);

    const replacement = `${this.$contactsDatatable.attr("data-source").split("?")[0]}?${$.param(params)}`;
    this.$contactsDatatable.attr("data-source", replacement);
    this.$contactsDatatable.DataTable().ajax.url(replacement).load(() => ProgressBar.stop());
  }

  currentParams() {
    return this.urlParamsAsObject(this.$contactsDatatable.attr("data-source"));
  }

  // `$.param()` re-serializes nested objects back into bracket notation
  // (filter[gift_status][]=...), so a flat URLSearchParams parse would lose the nesting.
  urlParamsAsObject(dataSource) {
    const query = dataSource.substring(dataSource.indexOf("?") + 1);
    const regex = /([^&=]+)=?([^&]*)/g;
    const decode = (str) => decodeURIComponent(str.replace(/\+/g, " "));

    const params = {};
    let match = regex.exec(query);
    while (match) {
      const k = decode(match[1]);
      const v = decode(match[2]);

      if (k.substring(k.length - 2) === "[]") {
        const key = k.substring(0, k.length - 2);
        (params[key] || (params[key] = [])).push(v);
      } else {
        params[k] = v;
      }

      match = regex.exec(query);
    }

    const assignPath = (obj, keyPath, value) => {
      const lastKeyIndex = keyPath.length - 1;
      let target = obj;
      for (let i = 0; i < lastKeyIndex; i++) {
        const key = keyPath[i];
        if (!(key in target)) { target[key] = {}; }
        target = target[key];
      }
      target[keyPath[lastKeyIndex]] = value;
    };

    Object.keys(params).forEach((prop) => {
      const structure = prop.split("[");
      if (structure.length > 1) {
        const levels = structure.map((part) => part.replace(/[?[\]\\ ]/g, ""));
        assignPath(params, levels, params[prop]);
        delete params[prop];
      }
    });

    return params;
  }

  getTotalRecordsCount() {
    return this.contactsDataTable.filteredLength();
  }

  getPageRecordsCount() {
    return this.contactsDataTable.pageLength();
  }

  getPageNumber() {
    return this.contactsDataTable.currentPageNumber();
  }

  toggleActionButton() {
    return this.$addSelectedButton.attr("disabled", this.getNumCheckedContacts() <= 0);
  }

  setCheckedCount() {
    this.element.find(".select-all .badge").text(`(${this.getNumCheckedContacts()})`);
    this.toggleActionButton();
  }

  getNumCheckedContacts() {
    return this.allCheckboxChecked ? this.getTotalRecordsCount() - this.exceptIds.size : this.sendToIds.size;
  }

  searchTerm() {
    return this.contactsDataTable.searchTerm();
  }

  alertError(message) {
    window.ltAlert({ message: message, type: "toast-error", duration: 5000 });
  }

  resetSelections() {
    this.sendToIds.clear();
    this.exceptIds.clear();
    this.allCheckboxChecked = false;
    this.pageCheckboxCheckedNum = null;
    this.setCheckedCount();
  }

  closeModal() {
    this.resetSelections();
    this.$filterEvent.val(null).trigger("change");
    this.$filterContactList.val(null).trigger("change");
    this.$dateRangePicker.data("daterangepicker").setStartDate(moment().format("MM/DD/YYYY"));
    this.$contactsDatatable.DataTable().ajax.url(this.dataTableInitialSource).load();
    this.$onNextEventCheckbox.prop("checked", true);
    this.element.modal("hide");
  }
}
